import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { useDebounce } from '../../../../../hooks';
import { getSegments } from '../services';
import { useVisaoGeralElasticidadeStore } from '../VisaoGeralElasticidade.store';
import type { VisaoGeralElasticidadeFiltroSegmentoResponse } from '../types';

export const useSegmentsFilter = () => {
  const filtersBody = useVisaoGeralElasticidadeStore(state => state.filtersBody);
  const updateSharedFilters = useVisaoGeralElasticidadeStore.getState().updateSharedFilters;
  const debounce = useDebounce(500);

  const [segmentsData, setSegmentsData] = useState<VisaoGeralElasticidadeFiltroSegmentoResponse[]>([]);
  const [subsegmentSearchQuery, setSubsegmentSearchQuery] = useState('');
  const filtersBodyRef = useRef(filtersBody);

  filtersBodyRef.current = filtersBody;

  const mutation = useMutation({
    mutationFn: async (query = '') => {
      const response = (await getSegments({
        ...filtersBodyRef.current,
        query,
      })) as VisaoGeralElasticidadeFiltroSegmentoResponse[];

      setSegmentsData(response ?? []);
      return response;
    },
  });

  const fetchSegments = useCallback(
    (query = '') => {
      mutation.mutate(query);
    },
    [mutation],
  );

  const segmentOptions = useMemo(() => segmentsData.map(item => item.segmento), [segmentsData]);

  const subsegmentOptions = useMemo(() => {
    const selectedSegment = segmentsData.find(item => item.segmento.value === filtersBody.segmentation);
    const options = selectedSegment?.subsegmentos ?? [];

    if (!subsegmentSearchQuery) return options;

    const normalizedQuery = subsegmentSearchQuery.toLowerCase();
    return options.filter(option => option.label.toLowerCase().includes(normalizedQuery));
  }, [filtersBody.segmentation, segmentsData, subsegmentSearchQuery]);

  const handleSegmentOpen = useCallback(() => {
    fetchSegments('');
  }, [fetchSegments]);

  const handleSubsegmentOpen = useCallback(() => {
    setSubsegmentSearchQuery('');
    fetchSegments('');
  }, [fetchSegments]);

  const onSearchSegment = useCallback(
    (inputValue: string) => {
      debounce(() => {
        fetchSegments(inputValue);
      });
    },
    [debounce, fetchSegments],
  );

  const onSearchSubsegment = useCallback((inputValue: string) => {
    setSubsegmentSearchQuery(inputValue);
  }, []);

  const handleSegmentSelect = useCallback(
    (value: string) => {
      updateSharedFilters({
        segmentation: value ?? '',
        subSegmentation: '',
      });
      setSubsegmentSearchQuery('');
    },
    [updateSharedFilters],
  );

  const handleSubsegmentSelect = useCallback(
    (value: string) => {
      updateSharedFilters({ subSegmentation: value ?? '' });
    },
    [updateSharedFilters],
  );

  const handleSegmentClear = useCallback(() => {
    updateSharedFilters({
      segmentation: '',
      subSegmentation: '',
    });
    setSubsegmentSearchQuery('');
  }, [updateSharedFilters]);

  const handleSubsegmentClear = useCallback(() => {
    updateSharedFilters({ subSegmentation: '' });
    setSubsegmentSearchQuery('');
  }, [updateSharedFilters]);

  const handleClearFilters = useCallback(() => {
    handleSegmentClear();
  }, [handleSegmentClear]);

  const fetchSegmentsRef = useRef(fetchSegments);
  fetchSegmentsRef.current = fetchSegments;

  useEffect(() => {
    fetchSegmentsRef.current('');
  }, []);

  return {
    segmentation: filtersBody.segmentation || null,
    subSegmentation: filtersBody.subSegmentation || null,
    segmentOptions,
    subsegmentOptions,
    isSubsegmentDisabled: !filtersBody.segmentation,
    isLoading: mutation.isPending,
    filterHandlers: {
      handleSegmentOpen,
      handleSubsegmentOpen,
      onSearchSegment,
      onSearchSubsegment,
      handleSegmentSelect,
      handleSubsegmentSelect,
      handleSegmentClear,
      handleSubsegmentClear,
      handleClearFilters,
    },
  };
};
